import { useEffect, useMemo, useState } from "react";
import useSystemClock from "modules/system/hooks/useSystemClock";
import { IconButtonWithTooltip } from "@remo-co/ui-core/src/components/IconButtonWithTooltip";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Replay } from "@remo-co/ui-core/src/icons/Replay";
import { useI18n } from "i18n";
import { durationTimestamp } from "helpers/time/format";
import { deltaToDuration } from "helpers/time/operations";
import { useSelector } from "react-redux";
import { selectCurrentEvent, selectEventState } from "modules/event/selectors";
import { EventStates } from "modules/event/constants";
import { useStyles } from "modules/broadcast/BroadcastEventTimerStyles";
import { selectIsMobile } from "modules/deviceInfo";
import { isRecentlyHappened } from "helpers/time/comparison";
import { useRestartEvent } from "../../hooks/useRestartEvent";

interface Props {
  showTimer?: (show: boolean) => void;
}

const EventCountdownTimer = ({ showTimer: showTimerCallback }: Props) => {
  const { t } = useI18n(["event"]);
  const currentEvent = useSelector(selectCurrentEvent);
  const { restartEvent } = useRestartEvent(currentEvent?.id);
  const startTime = currentEvent?.startTime ?? 0;
  const endTime = currentEvent?.endTime ?? 0;
  const eventState = useSelector(selectEventState);
  const { getCurrentTime } = useSystemClock();
  const isMobileView = useSelector(selectIsMobile);
  const eventDuration = endTime - startTime;
  const [time, setTime] = useState<number>(eventDuration);
  const ended = time <= 0;
  const styles = useStyles({ ended });

  const oneHour = 60 * 60 * 1000;

  // Shows timer if remaining time is less than 1 hour
  // and the event already started
  const showTimer =
    time <= oneHour &&
    (eventState === EventStates.IN_PROGRESS ||
      eventState === EventStates.ENDED);

  // Tick timer
  useEffect(() => {
    let timerId: number | undefined;

    switch (eventState) {
      case EventStates.UPCOMING: {
        setTime(eventDuration);
        break;
      }
      case EventStates.IN_PROGRESS: {
        timerId = window.setInterval(() => {
          const newTime = Math.max(
            Math.min(eventDuration, endTime - getCurrentTime()),
            0,
          );

          setTime(newTime);
        }, 500);
        break;
      }
      default: {
        setTime(0);
        break;
      }
    }

    return () => clearInterval(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDuration, eventState, endTime]);

  // Show Timer
  useEffect(() => {
    if (!showTimerCallback) return;

    showTimerCallback(showTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTimer]);

  const formatTime = (unformattedTime: number) =>
    durationTimestamp(
      deltaToDuration(unformattedTime),
      !isMobileView && unformattedTime >= oneHour,
    );

  const showRestartButton = useMemo(
    () => ended && !isRecentlyHappened(new Date(), startTime, 1440),
    [ended, startTime],
  );

  if (!showTimer) return null;

  return (
    <div data-testid="event-progress" className={styles.timer}>
      <Typography color="inherit">{formatTime(time)}</Typography>
      {showRestartButton && (
        <IconButtonWithTooltip
          id="restart-event-icon-button"
          title={t("event:restart.event")}
          size="small"
          className={styles.restartButton}
          onClick={() => restartEvent()}
          data-testid="restart-event-btn"
        >
          <Replay />
        </IconButtonWithTooltip>
      )}
    </div>
  );
};

export default EventCountdownTimer;
